import { getCoreUtils } from "../../../Admin/kReusableCoreTS/kCoreHandler";
import { AppAccountPost, AppRawDone, AppRawOptions } from "./AppAccountPost";
import { AppChatArchiveWho } from "./AppChatArchiveRules";
import { IAppChatArchiveCall, AppChatArchiveAsk } from "./AppChatArchive";
import { AppChatMark, AppChatMarksRules } from "./AppChatMarksRules";
import { AppSignUpApi } from "./AppSignUpApi";
import { IAppLiveClock, AppLiveWindowClock } from "./AppLiveFeed";

// AppChatMarks - THE PHONE'S MARKS ON THE CHAT LIST (2026-10-14, sub-agent `chatactions`): which chats are PINNED on the
// phone (and in what order), the phone's UNREAD count, Chat LOCK and the BLOCKLIST - `GET /chat/settings?view=marks`
// (Bearer key; the service's `ChatActions.Marks`, fed by rc14's `chats.upsert/update`, history, `chats.lock` and the
// blocklist). One read when the list is drawn and every `EveryMs` while the tab is visible; read-only.
// ⭐ MERGED, NOT REPLACED: WACommander's own "N new" (`AppContact.Unread`, from the live list) and the phone's count are
// shown as ONE badge - the larger; a chat marked unread with no count shows "Unread". Pinned chats come first, in the
// phone's pin order (newest pin first), each with a pin icon (`AppIcons.Pin`).
// ⭐ CHAT LOCK - DECIDED: a lock icon only; nothing is hidden. A row in WACommander carries no message words (standing,
// age, badges), and the archive is opened only with the owner's own key, so there is no preview to hide - hiding the
// row would make the chat vanish from the owner's own archive, which Chat Lock never does on the phone either (it
// moves the chat into a locked folder the owner can still open).

export interface AppChatMarksHttp {
    new(Url: string, Body: string, Done: AppRawDone, Options: AppRawOptions): IAppChatArchiveCall;
}

/** The reader: one call in flight, a timer while visible, every answer handed to `Changed`. */
export class AppChatMarks {
    public Marks: Map<string, AppChatMark> = new Map<string, AppChatMark>();
    private Call: IAppChatArchiveCall | null = null;
    private Timer: number = 0;
    private Version: number = 0;
    private Active: boolean = false;
    public constructor(private Ask: AppChatArchiveAsk, private Changed: () => void, private Http: AppChatMarksHttp = AppAccountPost,
                       private Clock: IAppLiveClock = new AppLiveWindowClock()) { }

    /** Read now (a list was drawn, a setting changed) and keep reading while visible. */
    public Load = (): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            this.Active = true;
            if (this.Call !== null) { return; }
            this.Stop(false);
            const Who: AppChatArchiveWho = this.Ask();
            const Base: string = AppSignUpApi.UrlFor(Who.ServiceUrl, "/chat/settings");
            if (Base.length === 0 || Who.Key.length === 0 || Who.Number.length === 0) { return; }
            const Options: AppRawOptions = new AppRawOptions(); Options.Method = "GET"; Options.TimeoutMs = 10000;
            Options.Headers.Authorization = "Bearer " + Who.Key;
            this.Version = this.Version + 1;
            this.Call = new this.Http(AppChatMarksRules.Url(Base, Who.Number), "", new AppChatMarksAnswer(this, this.Version).Heard, Options);
            this.Call.Send();
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatMarks.Load");
        }
    };

    public Answer = (Version: number, Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try {
            if (Version !== this.Version || this.Call === null) { return; }
            this.Call = null;
            const Got: Map<string, AppChatMark> | null = AppChatMarksRules.Parse(Status, Text);
            if (Got !== null) { this.Marks = Got; this.Changed(); }
            if (this.Active === true) { this.Timer = this.Clock.Later(AppChatMarksRules.EveryMs, this.Load); }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatMarks.Answer");
        }
    };

    /** Hidden tab / signed out: no more reads. `Halt` false = only the pending timer and call go (a new read follows). */
    public Stop = (Halt: boolean = true): void => {
        // ERROR-SUPPRESSED-JUSTIFIED: no KErrorMode guard - stopping timers and calls is cleanup.
        try {
            if (Halt === true) { this.Active = false; }
            if (this.Timer !== 0) { this.Clock.Cancel(this.Timer); this.Timer = 0; }
            if (Halt === true && this.Call !== null) { this.Version = this.Version + 1; this.Call.Abort(); this.Call = null; }
        } catch (e) {
            getCoreUtils().KGlobalErrorHandler(e, "AppChatMarks.Stop");
        }
    };
}

class AppChatMarksAnswer {
    public constructor(private Owner: AppChatMarks, private Version: number) { }
    public Heard = (Status: number, Text: string): void => {
        if (getCoreUtils().KErrorMode === true) { return; }
        try { this.Owner.Answer(this.Version, Status, Text); }
        catch (e) { getCoreUtils().KGlobalErrorHandler(e, "AppChatMarksAnswer.Heard"); }
    };
}
